import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Calendar, Users, MapPin, Plus, Trash2, UserPlus, X } from 'lucide-react';

function EventManager({ registrations = [] }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showParticipantsDialog, setShowParticipantsDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [newEvent, setNewEvent] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    event_type: 'cohort',
    max_participants: 30,
    location: 'Online',
    status: 'upcoming'
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const createEvent = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(newEvent)
      });
      
      if (response.ok) {
        toast.success('Event created successfully!');
        setShowCreateDialog(false);
        setNewEvent({
          name: '',
          description: '',
          start_date: '',
          end_date: '',
          event_type: 'cohort',
          max_participants: 30,
          location: 'Online',
          status: 'upcoming'
        });
        fetchEvents();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to create event');
      }
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Event deleted');
        fetchEvents();
      }
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const fetchParticipants = async (eventId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${eventId}/participants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setParticipants(data.participants || []);
      }
    } catch (error) {
      console.error('Failed to fetch participants');
    }
  };

  const addParticipant = async (userId) => {
    if (!selectedEvent) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${selectedEvent.id}/participants`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ user_id: userId })
      });
      
      if (response.ok) {
        toast.success('Participant added!');
        fetchParticipants(selectedEvent.id);
        fetchEvents();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to add participant');
      }
    } catch (error) {
      toast.error('Failed to add participant');
    }
  };

  const removeParticipant = async (userId) => {
    if (!selectedEvent) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${selectedEvent.id}/participants/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Participant removed');
        fetchParticipants(selectedEvent.id);
        fetchEvents();
      }
    } catch (error) {
      toast.error('Failed to remove participant');
    }
  };

  const openParticipants = (event) => {
    setSelectedEvent(event);
    fetchParticipants(event.id);
    setShowParticipantsDialog(true);
  };

  const getStatusBadge = (status) => {
    const colors = {
      upcoming: 'bg-blue-100 text-blue-800',
      active: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[status] || colors.upcoming}>{status}</Badge>;
  };

  const getTypeBadge = (type) => {
    const colors = {
      cohort: 'bg-purple-100 text-purple-800',
      workshop: 'bg-amber-100 text-amber-800',
      retreat: 'bg-teal-100 text-teal-800'
    };
    return <Badge className={colors[type] || colors.cohort}>{type}</Badge>;
  };

  const availableRegistrations = registrations.filter(reg => {
    return !participants.find(p => p.user_id === reg.id);
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Event Management</h2>
          <p className="text-gray-600">Create events and assign participants</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700 text-white" style={{ borderRadius: 0 }}>
              <Plus className="w-4 h-4 mr-2" />
              Create Event
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" style={{ borderRadius: 0 }}>
            <DialogHeader>
              <DialogTitle>Create New Event</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Event Name *</Label>
                <Input
                  value={newEvent.name}
                  onChange={(e) => setNewEvent({...newEvent, name: e.target.value})}
                  placeholder="e.g., Spring 2025 Cohort"
                  className="border-black"
                  style={{ borderRadius: 0 }}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                  placeholder="Brief description..."
                  className="border-black"
                  style={{ borderRadius: 0 }}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={newEvent.start_date}
                    onChange={(e) => setNewEvent({...newEvent, start_date: e.target.value})}
                    className="border-black"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={newEvent.end_date}
                    onChange={(e) => setNewEvent({...newEvent, end_date: e.target.value})}
                    className="border-black"
                    style={{ borderRadius: 0 }}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Event Type</Label>
                  <select
                    value={newEvent.event_type}
                    onChange={(e) => setNewEvent({...newEvent, event_type: e.target.value})}
                    className="w-full p-2 border border-black"
                  >
                    <option value="cohort">Cohort</option>
                    <option value="workshop">Workshop</option>
                    <option value="retreat">Retreat</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Max Participants</Label>
                  <Input
                    type="number"
                    value={newEvent.max_participants}
                    onChange={(e) => setNewEvent({...newEvent, max_participants: parseInt(e.target.value)})}
                    className="border-black"
                    style={{ borderRadius: 0 }}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                    placeholder="Online or physical"
                    className="border-black"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <select
                    value={newEvent.status}
                    onChange={(e) => setNewEvent({...newEvent, status: e.target.value})}
                    className="w-full p-2 border border-black"
                  >
                    <option value="upcoming">Upcoming</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
              
              <Button 
                onClick={createEvent}
                className="w-full bg-black text-white hover:bg-black/90"
                style={{ borderRadius: 0 }}
                disabled={!newEvent.name || !newEvent.start_date}
              >
                Create Event
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading events...</div>
      ) : events.length === 0 ? (
        <Card style={{ borderRadius: 0 }}>
          <CardContent className="py-12 text-center">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-xl font-semibold mb-2">No Events Yet</h3>
            <p className="text-gray-600 mb-4">Create your first event to start organizing participants</p>
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-green-600 hover:bg-green-700 text-white"
              style={{ borderRadius: 0 }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Event
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((event) => (
            <Card key={event.id} style={{ borderRadius: 0 }} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{event.name}</CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => deleteEvent(event.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex gap-2">
                  {getTypeBadge(event.event_type)}
                  {getStatusBadge(event.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {event.description && (
                  <p className="text-sm text-gray-600 line-clamp-2">{event.description}</p>
                )}
                
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    {new Date(event.start_date).toLocaleDateString()}
                    {event.end_date && ` - ${new Date(event.end_date).toLocaleDateString()}`}
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    {event.location}
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Users className="w-4 h-4" />
                    {event.participant_count} / {event.max_participants} participants
                  </div>
                </div>
                
                <Button 
                  onClick={() => openParticipants(event)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  style={{ borderRadius: 0 }}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Manage Participants
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showParticipantsDialog} onOpenChange={setShowParticipantsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" style={{ borderRadius: 0 }}>
          <DialogHeader>
            <DialogTitle>
              Participants - {selectedEvent?.name}
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({participants.length} / {selectedEvent?.max_participants})
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid md:grid-cols-2 gap-6 mt-4">
            <div>
              <h3 className="font-semibold mb-3">Current Participants</h3>
              {participants.length === 0 ? (
                <p className="text-gray-500 text-sm">No participants yet</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {participants.map((p) => (
                    <div key={p.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium">{p.name}</p>
                        <p className="text-xs text-gray-500">{p.email}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeParticipant(p.user_id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div>
              <h3 className="font-semibold mb-3">Add from Registrations</h3>
              {availableRegistrations.length === 0 ? (
                <p className="text-gray-500 text-sm">All registrations have been added</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {availableRegistrations.slice(0, 20).map((reg) => (
                    <div key={reg.id} className="flex justify-between items-center p-2 bg-blue-50 rounded">
                      <div>
                        <p className="font-medium">{reg.full_name}</p>
                        <p className="text-xs text-gray-500">{reg.email}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => addParticipant(reg.id)}
                        className="text-green-600 hover:text-green-700"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {availableRegistrations.length > 20 && (
                    <p className="text-xs text-gray-500 text-center">
                      +{availableRegistrations.length - 20} more
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default EventManager;